import React, { useState } from 'react'

// ---- Small reusable mock UI pieces (visual only, styled like the real portal) ----

function MockCard({ children }) {
  return (
    <div style={{ background:'linear-gradient(180deg,#fff 0%,#fff8fb 100%)', borderRadius:20, padding:'20px 18px', boxShadow:'0 4px 16px rgba(232,84,122,0.12)', border:'1px solid #fde3ec', minHeight:280 }}>
      {children}
    </div>
  )
}

function MockChecklistItem({ label, done }) {
  return (
    <div style={{ padding:'9px 12px', borderRadius:9, marginBottom:4, fontSize:13, display:'flex', alignItems:'center', gap:8,
      background: done ? '#e8547a' : '#f1f3f4', color: done ? '#fff' : '#5f6368' }}>
      <span>{done ? '✓' : '○'}</span>{label}
    </div>
  )
}

function MockButton({ children, color='#1d9e75' }) {
  return (
    <div style={{ width:'100%', padding:15, borderRadius:14, background:color, color:'#fff', fontWeight:800, fontSize:15, textAlign:'center' }}>
      {children}
    </div>
  )
}

function Caption({ children }) {
  return (
    <div style={{ marginTop:16, padding:'12px 16px', background:'#fff8e1', border:'1px solid #ffe082', borderRadius:12, fontSize:13, color:'#5a4000', lineHeight:1.5 }}>
      💡 {children}
    </div>
  )
}

// ---- Scenario content ----

const REGULAR_STEPS = [
  {
    caption: 'Al llegar a la casa, presiona Clock In para marcar tu hora de llegada.',
    mock: (
      <MockCard>
        <div style={{ fontSize:11, fontWeight:800, color:'#e8547a', textTransform:'uppercase', marginBottom:6 }}>🏠 Casa actual</div>
        <div style={{ fontSize:22, fontWeight:800, color:'#3a2530', marginBottom:20 }}>Familia Gómez</div>
        <MockButton>🟢 Clock In</MockButton>
      </MockCard>
    ),
  },
  {
    caption: 'Antes de empezar, tienes 30 segundos para leer el checklist y las notas del cliente mientras haces un recorrido rápido de la casa.',
    mock: (
      <MockCard>
        <div style={{ fontSize:11, fontWeight:800, color:'#e8547a', textTransform:'uppercase', marginBottom:8 }}>🧹 Regular</div>
        <div style={{ padding:'8px 10px', background:'#fdeef3', borderRadius:8, fontSize:12, color:'#5a1a2a', marginBottom:10 }}>📋 No usar productos con cloro</div>
        <MockChecklistItem label="Vacuum all rooms" />
        <MockChecklistItem label="Mopping hard floors" />
        <MockChecklistItem label="Cleaning bathrooms" />
        <div style={{ textAlign:'center', margin:'12px 0' }}>
          <div style={{ fontSize:26, fontWeight:800, color:'#e8547a' }}>22</div>
        </div>
        <MockButton color="#e3e5e8"><span style={{color:'#9aa0a6'}}>Espera 22s...</span></MockButton>
      </MockCard>
    ),
  },
  {
    caption: 'Después, dinos cuántas horas crees que tomará. Sé honesta — Yosi lo revisa antes de que sigas.',
    mock: (
      <MockCard>
        <div style={{ textAlign:'center' }}>
          <div style={{ fontSize:16, fontWeight:800, color:'#3a2530', marginBottom:20 }}>¿Cuántas horas crees que tomará?</div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:20, marginBottom:24 }}>
            <div style={{ width:44, height:44, borderRadius:'50%', background:'#e3e5e8', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, fontWeight:800, color:'#5f6368' }}>−</div>
            <div style={{ fontSize:32, fontWeight:800, color:'#e8547a' }}>2.5h</div>
            <div style={{ width:44, height:44, borderRadius:'50%', background:'#e3e5e8', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, fontWeight:800, color:'#5f6368' }}>+</div>
          </div>
          <MockButton color="#e8547a">Confirmar</MockButton>
        </div>
      </MockCard>
    ),
  },
  {
    caption: 'Si crees que va a tomar mucho más tiempo del esperado, te pedirá una razón y una foto opcional — así Yosi entiende por qué antes de aprobarlo.',
    mock: (
      <MockCard>
        <div style={{ fontSize:15, fontWeight:800, color:'#3a2530', textAlign:'center', marginBottom:12 }}>Va a tomar más tiempo de lo esperado</div>
        <div style={{ display:'flex', gap:6, marginBottom:12, flexWrap:'wrap' }}>
          {['Polvoso','Grasoso','Sucio'].map(t=>(
            <div key={t} style={{ padding:'6px 12px', borderRadius:99, background:'#e8547a', color:'#fff', fontSize:11, fontWeight:600 }}>{t}</div>
          ))}
        </div>
        <div style={{ padding:10, background:'#f1f3f4', borderRadius:8, fontSize:12, color:'#80868b', marginBottom:12 }}>"Cocina estaba mucho más sucia de lo normal..."</div>
        <MockButton color="#e8547a">Enviar</MockButton>
      </MockCard>
    ),
  },
  {
    caption: 'Mientras Yosi revisa tu estimado, puedes seguir limpiando. Sabrás si fue aprobado en la pestaña 🔔 Estado.',
    mock: (
      <MockCard>
        <div style={{ textAlign:'center', paddingTop:20 }}>
          <div style={{ fontSize:40, marginBottom:10 }}>📲</div>
          <div style={{ fontSize:16, fontWeight:800, color:'#3a2530', marginBottom:10 }}>¡Gracias! Tu estimado fue enviado</div>
          <div style={{ fontSize:12, color:'#a98a97' }}>Yosi lo va a revisar. Mientras tanto, puedes seguir con la limpieza.</div>
        </div>
      </MockCard>
    ),
  },
  {
    caption: 'Antes de empezar a limpiar, envía las fotos de "antes" por WhatsApp y confirma aquí que ya lo hiciste.',
    mock: (
      <MockCard>
        <div style={{ textAlign:'center', paddingTop:10 }}>
          <div style={{ fontSize:40, marginBottom:10 }}>📸</div>
          <div style={{ fontSize:16, fontWeight:800, color:'#3a2530', marginBottom:8 }}>¿Ya enviaste las fotos de ANTES?</div>
          <div style={{ fontSize:12, color:'#c05050', background:'#fff3f3', border:'1px solid #f4b8b8', borderRadius:10, padding:'8px 12px', marginBottom:16 }}>
            📌 Recuerda: solo tomamos fotos de la cocina y los baños.
          </div>
          <MockButton>✓ Sí, ya las envié</MockButton>
        </div>
      </MockCard>
    ),
  },
  {
    caption: 'Marca cada tarea conforme la terminas. No podrás firmar hasta completar todo el checklist, incluyendo las notas del cliente.',
    mock: (
      <MockCard>
        <div style={{ padding:'8px 10px', background:'#fdeef3', borderRadius:8, marginBottom:10 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}><span>☑</span><span style={{ fontSize:12, color:'#5a1a2a', textDecoration:'line-through' }}>No usar productos con cloro</span></div>
        </div>
        <MockChecklistItem label="Vacuum all rooms" done />
        <MockChecklistItem label="Mopping hard floors" done />
        <MockChecklistItem label="Cleaning bathrooms" />
        <div style={{ marginTop:10 }}><MockButton color="#f0f0f0"><span style={{color:'#9aa0a6'}}>Completa todo para firmar</span></MockButton></div>
      </MockCard>
    ),
  },
  {
    caption: 'Una vez todo esté marcado, escribe tus iniciales para firmar.',
    mock: (
      <MockCard>
        <div style={{ textAlign:'center', paddingTop:30 }}>
          <div style={{ fontSize:14, fontWeight:700, color:'#3a2530', marginBottom:16 }}>Escribe tus iniciales</div>
          <div style={{ width:100, margin:'0 auto 20px', padding:'12px 0', border:'2px solid #e8547a', borderRadius:10, fontSize:22, fontWeight:800, color:'#e8547a' }}>MG</div>
          <MockButton color="#e8547a">✓ Confirmar firma</MockButton>
        </div>
      </MockCard>
    ),
  },
  {
    caption: '¡Listo! Presiona Clock Out para marcar tu salida.',
    mock: (
      <MockCard>
        <div style={{ textAlign:'center', paddingTop:20 }}>
          <div style={{ fontSize:12, color:'#1d9e75', fontWeight:700, marginBottom:16 }}>✓ Checklist firmado · MG</div>
          <MockButton color="#e8547a">🔴 Clock Out</MockButton>
        </div>
      </MockCard>
    ),
  },
  {
    caption: 'Si tienes otra casa después, dinos si vas a tomar un descanso o si estás lista para empezar antes, y a qué hora crees que llegas.',
    mock: (
      <MockCard>
        <div style={{ fontSize:16, fontWeight:800, color:'#3a2530', textAlign:'center', marginBottom:14 }}>¿Cómo sigues?</div>
        <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:12 }}>
          <div style={{ padding:12, borderRadius:10, background:'#e8547a', color:'#fff', fontSize:12, fontWeight:700, textAlign:'center' }}>🕐 Llegaré a tiempo</div>
          <div style={{ padding:12, borderRadius:10, background:'#f1f3f4', color:'#80868b', fontSize:12, fontWeight:700, textAlign:'center' }}>☕ Voy antes, descanso</div>
        </div>
        <div style={{ padding:10, border:'1px solid #e3e5e8', borderRadius:8, textAlign:'center', fontSize:13, color:'#80868b' }}>🕐 2:30 PM</div>
      </MockCard>
    ),
  },
]

const AIRBNB_STEPS = [
  {
    caption: 'En un Airbnb, no necesitas hacer el recorrido de 30 segundos como en una casa regular — pero sí debes seguir la lista específica de Airbnb Turnover.',
    mock: (
      <MockCard>
        <div style={{ fontSize:11, fontWeight:800, color:'#e8547a', textTransform:'uppercase', marginBottom:8 }}>🏠 Airbnb Turnover</div>
        <MockChecklistItem label="Strip and remake all beds hotel-style" />
        <MockChecklistItem label="Fully sanitize all bathrooms" />
        <MockChecklistItem label="Check for guest left items" />
        <div style={{ marginTop:12 }}><MockButton>✓ Ya hice el recorrido — Continuar</MockButton></div>
      </MockCard>
    ),
  },
  {
    caption: 'Para Airbnb, las fotos son de TODOS los cuartos — especialmente el relleno/suministros, las toallas, y los botes de basura abiertos para mostrar que están vacíos.',
    mock: (
      <MockCard>
        <div style={{ textAlign:'center', paddingTop:10 }}>
          <div style={{ fontSize:40, marginBottom:10 }}>📸</div>
          <div style={{ fontSize:16, fontWeight:800, color:'#3a2530', marginBottom:8 }}>¿Ya enviaste las fotos de ANTES?</div>
          <div style={{ fontSize:12, color:'#c05050', background:'#fff3f3', border:'1px solid #f4b8b8', borderRadius:10, padding:'10px 12px', marginBottom:16, textAlign:'left' }}>
            📌 Recuerda: fotos de TODOS los cuartos — especialmente el relleno/suministros, las toallas, y los botes de basura (abiertos).
          </div>
          <MockButton>✓ Sí, ya las envié</MockButton>
        </div>
      </MockCard>
    ),
  },
  {
    caption: 'Al firmar, si algo del relleno se acabó (papel de baño, jabón, etc.), anótalo aquí para que Yosi lo pueda ordenar antes de la próxima visita.',
    mock: (
      <MockCard>
        <div style={{ fontSize:11, fontWeight:700, color:'#80868b', marginBottom:6 }}>📦 ¿Faltó algún relleno/suministro?</div>
        <div style={{ padding:10, background:'#f1f3f4', borderRadius:8, fontSize:12, color:'#5f6368', marginBottom:14 }}>"Papel de baño y jabón de manos"</div>
        <MockButton color="#e8547a">✍️ Firmar</MockButton>
      </MockCard>
    ),
  },
]

const DAMAGE_STEPS = [
  {
    caption: 'Si ves algo que ya estaba roto o dañado ANTES de que llegaras, repórtalo aquí — con una foto y explicación — así Yosi sabe que no fue tu equipo.',
    mock: (
      <MockCard>
        <div style={{ padding:'10px', borderRadius:10, border:'1px dashed #f4b8b8', background:'#fff8f8', color:'#c05050', fontSize:12, fontWeight:600, marginBottom:14 }}>
          📷 ¿Algo ya estaba dañado o roto? Repórtalo aquí
        </div>
        <div style={{ fontSize:11, color:'#80868b', textAlign:'center' }}>👆 Toca aquí para abrir el formulario</div>
      </MockCard>
    ),
  },
  {
    caption: 'Toma o sube una foto, explica qué encontraste, y envíalo. Esto queda guardado en tu registro de la visita — no afecta tu firma ni tu horario.',
    mock: (
      <MockCard>
        <div style={{ padding:'12px', background:'#fff8f8', borderRadius:10, border:'1px solid #f4b8b8' }}>
          <div style={{ fontSize:12, fontWeight:700, color:'#c05050', marginBottom:8 }}>📷 Reportar algo ya dañado</div>
          <div style={{ padding:10, border:'1px dashed #f4b8b8', borderRadius:8, textAlign:'center', fontSize:11, color:'#80868b', marginBottom:8 }}>📷 IMG_2481.jpg</div>
          <div style={{ padding:10, background:'#fff', borderRadius:8, fontSize:12, color:'#5f6368', marginBottom:8 }}>"El espejo del baño ya tenía esta grieta cuando llegué"</div>
          <MockButton color="#c05050">Enviar reporte</MockButton>
        </div>
      </MockCard>
    ),
  },
]

const SCENARIOS = [
  { id:'regular', label:'🏠 Casa regular', steps: REGULAR_STEPS },
  { id:'airbnb', label:'🏨 Airbnb', steps: AIRBNB_STEPS },
  { id:'damage', label:'📸 Reportar daño', steps: DAMAGE_STEPS },
]

export default function TrainingMode({ onExit }) {
  const [activeScenario, setActiveScenario] = useState(null)
  const [stepIndex, setStepIndex] = useState(0)
  const [completed, setCompleted] = useState([])

  function startScenario(id) {
    setActiveScenario(id)
    setStepIndex(0)
  }

  function finishScenario() {
    setCompleted(prev => prev.includes(activeScenario) ? prev : [...prev, activeScenario])
    setActiveScenario(null)
  }

  const scenario = SCENARIOS.find(s => s.id === activeScenario)

  if (scenario) {
    const step = scenario.steps[stepIndex]
    const isLast = stepIndex === scenario.steps.length - 1
    return (
      <div style={{ minHeight:'100vh', background:'#f8f9fa', fontFamily:'system-ui,sans-serif', padding:'20px 16px' }}>
        <div style={{ maxWidth:380, margin:'0 auto' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <button onClick={()=>setActiveScenario(null)} style={{ border:'none', background:'none', color:'#80868b', fontSize:13, cursor:'pointer' }}>← Salir</button>
            <div style={{ fontSize:12, fontWeight:700, color:'#e8547a' }}>{scenario.label} · {stepIndex+1}/{scenario.steps.length}</div>
          </div>

          {step.mock}
          <Caption>{step.caption}</Caption>

          <div style={{ display:'flex', gap:8, marginTop:16 }}>
            <button onClick={()=>setStepIndex(i=>Math.max(0,i-1))} disabled={stepIndex===0}
              style={{ flex:1, padding:13, borderRadius:12, border:'1px solid #e3e5e8', background:'#fff', color: stepIndex===0?'#ddd':'#5f6368', fontWeight:700, fontSize:14, cursor: stepIndex===0?'default':'pointer' }}>
              ← Anterior
            </button>
            <button onClick={()=> isLast ? finishScenario() : setStepIndex(i=>i+1)}
              style={{ flex:1, padding:13, borderRadius:12, border:'none', background:'#e8547a', color:'#fff', fontWeight:700, fontSize:14, cursor:'pointer' }}>
              {isLast ? '✓ Terminar' : 'Siguiente →'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ minHeight:'100vh', background:'#f8f9fa', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'system-ui,sans-serif', padding:20 }}>
      <div style={{ background:'#fff', borderRadius:20, padding:'32px 28px', width:340, boxShadow:'0 8px 40px rgba(0,0,0,0.1)' }}>
        <div style={{ textAlign:'center', marginBottom:24 }}>
          <div style={{ fontSize:40, marginBottom:8 }}>🎓</div>
          <div style={{ fontSize:19, fontWeight:800, color:'#3a2530' }}>Modo de práctica</div>
          <div style={{ fontSize:12, color:'#80868b', marginTop:4 }}>Elige un escenario para practicar</div>
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:20 }}>
          {SCENARIOS.map(s => (
            <button key={s.id} onClick={()=>startScenario(s.id)}
              style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 16px', borderRadius:14, border:'1px solid #e3e5e8',
                background: completed.includes(s.id) ? '#e1f5ee' : '#f8f9fa', cursor:'pointer', fontSize:14, fontWeight:700, color:'#3a2530' }}>
              <span>{s.label}</span>
              {completed.includes(s.id) && <span style={{ color:'#1d9e75', fontSize:13 }}>✓ Completado</span>}
            </button>
          ))}
        </div>

        {completed.length === SCENARIOS.length && (
          <div style={{ textAlign:'center', fontSize:13, fontWeight:700, color:'#1d9e75', marginBottom:16 }}>
            🎉 ¡Entrenamiento completo! Ya conoces todo el flujo.
          </div>
        )}

        <button onClick={onExit}
          style={{ width:'100%', padding:'12px', border:'none', borderRadius:12, background:'#e3e5e8', color:'#5f6368', fontWeight:700, fontSize:13, cursor:'pointer' }}>
          ← Volver a iniciar sesión
        </button>
      </div>
    </div>
  )
}
